import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Truck, MapPin, Package, TrendingUp, Zap, ShoppingBag, Database, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";
import FAQSection from "@/components/FAQSection";

const DOM = () => {
  useSEO({
    title: 'Distributed Order Management (DOM) System | Ommni',
    description: 'Optimize order fulfillment with intelligent routing, multi-location management, and real-time visibility. Ommni DOM helps you fulfill orders faster and more efficiently.',
    keywords: 'distributed order management, order management system, order routing, fulfillment optimization, omnichannel fulfillment',
    canonical: 'https://ommni.io/features/dom',
  });

  const features = [
    {
      icon: MapPin,
      title: "Intelligent Order Routing",
      description: "Automatically route orders to the optimal fulfillment location based on inventory, proximity, and cost.",
    },
    {
      icon: Package,
      title: "Multi-Location Fulfillment",
      description: "Fulfill orders from warehouses, stores, drop-shippers, and any location in your network.",
    },
    {
      icon: Truck,
      title: "Real-Time Inventory Visibility",
      description: "See available inventory across all locations in real-time for accurate order promising.",
    },
    {
      icon: Zap,
      title: "Split Order Management",
      description: "Automatically split orders across multiple locations to optimize fulfillment speed and cost.",
    },
    {
      icon: ShoppingBag,
      title: "Buy Online, Pick Up In Store",
      description: "Enable BOPIS, curbside pickup, and ship-from-store capabilities seamlessly.",
    },
    {
      icon: TrendingUp,
      title: "Performance Analytics",
      description: "Track fulfillment performance, costs, and delivery times across your entire network.",
    },
  ];

  const benefits = [
    {
      title: "Reduce Shipping Costs",
      description: "Fulfill from the closest location to minimize shipping distances and expenses.",
    },
    {
      title: "Improve Delivery Speed",
      description: "Get products to customers faster with optimized fulfillment routing.",
    },
    {
      title: "Maximize Inventory Efficiency",
      description: "Utilize inventory across all locations to reduce stockouts and overstock.",
    },
    {
      title: "Enhance Customer Experience",
      description: "Offer flexible fulfillment options and accurate delivery promises.",
    },
  ];

  const faqs = [
    {
      question: "What is Distributed Order Management (DOM)?",
      answer: "Distributed Order Management is a system that intelligently routes customer orders to the optimal fulfillment location based on factors like inventory availability, proximity to customer, shipping costs, and delivery speed requirements."
    },
    {
      question: "How does intelligent order routing work?",
      answer: "Ommni DOM uses configurable routing rules that consider real-time inventory, location proximity, shipping costs, carrier availability, and business priorities to automatically select the best fulfillment location for each order."
    },
    {
      question: "Can I fulfill orders from retail stores?",
      answer: "Yes, Ommni DOM fully supports ship-from-store, buy online pick up in store (BOPIS), and curbside pickup capabilities. You can use store inventory to fulfill online orders, reducing shipping costs and delivery times."
    },
    {
      question: "What if an order needs to be split across multiple locations?",
      answer: "Ommni DOM automatically handles split orders when inventory isn't available at a single location. The system optimizes split decisions to balance cost and delivery speed while maintaining customer communication."
    },
    {
      question: "How does Ommni DOM integrate with my existing systems?",
      answer: "Ommni DOM connects with your ecommerce platforms, ERPs, warehouse management systems (WMS), and shipping carriers through pre-built integrations and APIs. This ensures seamless order flow from placement to delivery."
    },
    {
      question: "Can I set custom routing rules based on my business needs?",
      answer: "Absolutely. Ommni DOM allows you to configure routing rules based on product type, customer location, shipping method, inventory levels, carrier preferences, and any other business-specific criteria to optimize your fulfillment strategy."
    }
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Ommni DOM - Distributed Order Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Optimize order fulfillment across your entire network. Route orders intelligently, reduce costs, and deliver faster with distributed order management.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Intelligent Order Routing",
            "Multi-Location Fulfillment",
            "Real-Time Inventory Visibility",
            "Split Order Management",
            "Buy Online, Pick Up In Store",
            "Performance Analytics"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Truck className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Distributed Order Management
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Optimize order fulfillment across your entire network. Route orders intelligently, reduce costs, 
              and deliver faster with distributed order management.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Advanced DOM Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Key Benefits</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Package,
                title: "Inventory Management",
                description: "Real-time inventory visibility to power intelligent order routing decisions.",
                link: "/features/inventory",
              },
              {
                icon: Database,
                title: "Product Information Management",
                description: "Centralize product data for seamless order fulfillment across channels.",
                link: "/features/pim",
              },
              {
                icon: Workflow,
                title: "Workflow Automation",
                description: "Automate order routing and fulfillment processes with OmmniFlow.",
                link: "/features/ommni-flow",
              },
            ]}
          />

          {/* FAQ Section */}
          <FAQSection 
            faqs={faqs}
            title="Frequently Asked Questions"
            description="Common questions about Ommni's Distributed Order Management system."
          />

          {/* CTA Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Optimize Your Fulfillment Network
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Join leading retailers using Ommni DOM to reduce costs, improve delivery times, 
              and provide exceptional customer experiences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default DOM;
